import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { stripeService } from '../../../services/stripeService';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

// Inner form component that uses Stripe hooks
const AddCardForm = ({ clientSecret, onSuccess, onCancel, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(true);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      // Confirm the SetupIntent
      const { error: confirmError, setupIntent } = await stripe?.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location?.origin}/account`,
        },
      });

      if (confirmError) {
        setErrorMessage(confirmError?.message || 'Failed to add payment method');
        onError?.(confirmError);
        setIsProcessing(false);
        return;
      }

      if (setupIntent?.status === 'succeeded') {
        // Attach payment method via backend
        const { error: attachError } = await stripeService?.attachPaymentMethod(
          setupIntent?.id,
          setAsDefault
        );

        if (attachError) {
          setErrorMessage(attachError);
          onError?.(attachError);
        } else {
          onSuccess?.();
        }
      }
    } catch (error) {
      console.error('Add card error:', error);
      setErrorMessage(error?.message || 'An unexpected error occurred');
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stripe || !elements) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-muted rounded"></div>
        <div className="h-10 bg-muted rounded"></div>
        <p className="text-sm text-muted-foreground text-center">Loading payment form...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Card Details
        </label>
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {errorMessage && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start gap-2">
          <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{errorMessage}</p>
        </div>
      )}

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e?.target?.checked)}
          className="w-4 h-4 rounded border-border"
        />
        <span className="text-sm text-foreground">Set as default payment method</span>
      </label>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          fullWidth
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="default"
          fullWidth
          disabled={isProcessing || !stripe || !elements}
          iconName={isProcessing ? 'Loader2' : 'Plus'}
          iconClassName={isProcessing ? 'animate-spin' : ''}
          iconPosition="left"
        >
          {isProcessing ? 'Adding...' : 'Add Card'}
        </Button>
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
        <Icon name="Shield" size={12} />
        <span>Secured by Stripe • Your card information is encrypted</span>
      </div>
    </form>
  );
};

const PaymentMethods = ({ paymentMethods, userId, onUpdate, compact = false }) => {
  const [showAddCard, setShowAddCard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [initializingStripe, setInitializingStripe] = useState(false);

  const handleOpenAddCard = async () => {
    setInitializingStripe(true);
    setError(null);

    try {
      const { data, error: setupError } = await stripeService?.createSetupIntent();

      if (setupError) {
        setError(setupError);
        return;
      }

      setClientSecret(data?.clientSecret);
      setShowAddCard(true);
    } catch (err) {
      console.error('Setup intent error:', err);
      setError(err?.message || 'Failed to initialize payment form');
    } finally {
      setInitializingStripe(false);
    }
  };

  const handleCloseAddCard = () => {
    setShowAddCard(false);
    setClientSecret(null);
    setError(null);
  };

  const handleCardAdded = async () => {
    handleCloseAddCard();
    await onUpdate();
  };

  const handleSetDefault = async (methodId) => {
    setLoading(true);
    setError(null);

    const { error: updateError } = await stripeService?.setDefaultPaymentMethod(userId, methodId);

    if (updateError) {
      setError(updateError);
    } else {
      await onUpdate();
    }

    setLoading(false);
  };

  const handleRemove = async (methodId) => {
    if (!confirm('Are you sure you want to remove this payment method?')) return;

    setLoading(true);
    setError(null);

    const { error: removeError } = await stripeService?.removePaymentMethod(methodId);

    if (removeError) {
      setError(removeError);
    } else {
      await onUpdate();
    }

    setLoading(false);
  };

  const getCardBrandIcon = (brand) => {
    const brandMap = {
      visa: 'CreditCard',
      mastercard: 'CreditCard',
      amex: 'CreditCard',
      discover: 'CreditCard',
      default: 'CreditCard'
    };
    return brandMap?.[brand?.toLowerCase()] || brandMap?.default;
  };

  const displayedMethods = compact ? paymentMethods?.slice(0, 2) : paymentMethods;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="CreditCard" size={20} className="text-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Payment Methods</h3>
        </div>
        {!compact && (
          <Button
            variant="outline"
            size="sm"
            iconName={initializingStripe ? 'Loader2' : 'Plus'}
            iconClassName={initializingStripe ? 'animate-spin' : ''}
            iconPosition="left"
            onClick={handleOpenAddCard}
            disabled={initializingStripe}
          >
            {initializingStripe ? 'Loading...' : 'Add Card'}
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4 flex items-start gap-2">
          <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {displayedMethods?.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
            <Icon name="CreditCard" size={24} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground text-sm mb-4">No payment methods added</p>
          <Button
            variant="outline"
            size="sm"
            iconName={initializingStripe ? 'Loader2' : 'Plus'}
            iconClassName={initializingStripe ? 'animate-spin' : ''}
            iconPosition="left"
            onClick={handleOpenAddCard}
            disabled={initializingStripe}
          >
            {initializingStripe ? 'Loading...' : 'Add Payment Method'}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedMethods?.map((method) => (
            <div
              key={method?.id}
              className="flex items-center justify-between p-4 border border-border rounded-lg hover:border-primary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Icon name={getCardBrandIcon(method?.card_brand)} size={20} className="text-foreground" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground capitalize">
                      {method?.card_brand} •••• {method?.card_last_four}
                    </span>
                    {method?.is_default && (
                      <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full text-xs font-medium">
                        Default
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Expires {method?.card_exp_month?.toString()?.padStart(2, '0')}/{method?.card_exp_year}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!method?.is_default && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSetDefault(method?.id)}
                    disabled={loading}
                  >
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(method?.id)}
                  disabled={loading}
                  iconName="Trash2"
                />
              </div>
            </div>
          ))}

          {compact && paymentMethods?.length > 2 && (
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => {/* Navigate to payment tab */}}
            >
              View All ({paymentMethods?.length})
            </Button>
          )}
        </div>
      )}

      {/* Add Card Modal with Stripe Elements */}
      {showAddCard && clientSecret && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Add Payment Method</h3>
              <button
                onClick={handleCloseAddCard}
                className="p-1 hover:bg-muted rounded transition-colors"
                disabled={loading}
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: 'hsl(var(--primary))',
                    colorBackground: 'hsl(var(--card))',
                    colorText: 'hsl(var(--foreground))',
                    colorDanger: 'hsl(var(--destructive))',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              <AddCardForm
                clientSecret={clientSecret}
                onSuccess={handleCardAdded}
                onCancel={handleCloseAddCard}
                onError={(err) => setError(err?.message || 'Failed to add card')}
              />
            </Elements>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethods;